import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconPlus } from "@tabler/icons-react";

import MHeader from "@/components/ui/organisms/MHeader";
import MLabParameterCard from "@/components/ui/atoms/MLabParameterCard";
import MLabSnapshot from "@/components/ui/molecules/MLabSnapshot";
import MLoader from "@/components/ui/atoms/MLoader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { MDocumentUploadOptions } from "@/components/ui/molecules/MDocumentUploadOptions";
import EditPinsDrawer from "./EditPinsDrawer";
import { useLabInsightsLanding } from "@/hooks/useLabInsights";
import { useLabHealthSummary } from "@/hooks/useLabHealthSummary";
import { getMemberInfo } from "@/hooks/useMembers";
import { formatLabCardDate } from "@/utils/date-utils";
import { useNavigationSource } from "@/hooks/useNavigationSource";
import {
  LabParameterGroupId,
  ParameterStatus,
  PinnedParameterCard,
} from "@/types/lab-insights";

import styles from "./LabInsights.module.css";

// Product spec: the pin grid always shows 4 slots. Populated slots render
// the parameter card; the remainder render an empty-state placeholder that
// opens the Edit drawer when clicked. Once the member has 4+ distinct
// canonical parameters across their report history the API waterfall fills
// every slot, so this only fires for new members or single-report-with-few-
// parameters cases.
const PIN_SLOT_COUNT = 4;

const formatValue = (
  card: PinnedParameterCard
): { value: string | number | null; unit: string | null } => {
  const reading = card.latestReading;
  if (!reading) return { value: null, unit: null };
  if (reading.parsedResult !== null && reading.parsedResult !== undefined) {
    return { value: reading.parsedResult, unit: reading.unit };
  }
  return { value: reading.rawResult, unit: reading.unit };
};

const LabInsights: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const { navigateBackIfExists } = useNavigationSource();
  const { data, loading, error, updatePins } = useLabInsightsLanding(
    memberInfo?.id
  );

  const { insight: labSummary } = useLabHealthSummary(memberInfo?.id);

  const SCROLL_KEY = "labInsightsLandingScroll";
  const scrollRestored = useRef(false);

  const saveScroll = () => {
    const container = document.querySelector(
      "main.flex-1"
    ) as HTMLElement | null;
    if (container)
      sessionStorage.setItem(SCROLL_KEY, String(container.scrollTop));
  };

  // Restore scroll after data is ready and DOM has fully painted.
  // Double rAF ensures layout is complete before we set scrollTop.
  useEffect(() => {
    if (loading || !data || scrollRestored.current) return;
    scrollRestored.current = true;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (!saved) return;
    sessionStorage.removeItem(SCROLL_KEY);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = document.querySelector(
          "main.flex-1"
        ) as HTMLElement | null;
        if (container) container.scrollTop = Number(saved);
      });
    });
  }, [loading, data]);
  // Which pin slot is currently being edited. null = drawer closed.
  // The slot index is the 0-based grid position the user clicked Edit on;
  // when the user saves, we replace pinned[editingSlotIndex] with the
  // newly-picked parameter rather than blindly appending or replacing the
  // last entry. Slot indices >= pinned.length correspond to a blank
  // placeholder click (append a new pin into that slot).
  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
  const editPinsOpen = editingSlotIndex !== null;
  const [uploadOpen, setUploadOpen] = useState(false);

  const pinned = data?.pinnedParameters ?? [];
  const hasFlagged = pinned.some((card) => card.latestReading?.flagged);
  const constellation = data?.constellation ?? [];

  const hasAnyActiveGroup = constellation.some((g) => g.active);
  const isEmptyState =
    !loading && !error && !!data && !hasAnyActiveGroup && pinned.length === 0;
  const interpretationText =
    (typeof labSummary?.value?.interpretation === "string" &&
      labSummary.value.interpretation.trim()) ||
    labSummary?.summary ||
    "";
  const centerLabel =
    memberInfo?.nickname || memberInfo?.firstName || t("labInsights.title");

  // Landing-only: respect an external navigation source (e.g. family/member
  // detail) so the user goes back to where they originally launched Lab
  // Insights from. Internal pages (groups, parameters) follow URL/state-based
  // back navigation and do not consult the navigation source.
  const handleBack = () => {
    if (!navigateBackIfExists()) navigate("/dashboard");
  };
  const handleSeeAll = () => {
    saveScroll();
    navigate("/lab-insights/all-groups");
  };
  const handleGroupClick = (groupId: LabParameterGroupId) => {
    saveScroll();
    navigate(`/lab-insights/${groupId}`);
  };

  if (loading && !data) {
    return (
      <div className={styles.page}>
        <MHeader
          module="labInsights"
          subHeading={t("labInsights.title")}
          memberInfo={memberInfo}
          description={t("labInsights.subtitle")}
          showProfilePickerRow
          showGradient
          hideLearnMore
          onBack={handleBack}
          className={styles.flushHeader}
        />
        <MSpacer mt={8} />
        <MLoader />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <MHeader
        module="labInsights"
        subHeading={t("labInsights.title")}
        memberInfo={memberInfo}
        description={t("labInsights.subtitle")}
        showProfilePickerRow
        showGradient
        hideLearnMore
        onBack={handleBack}
        className={styles.flushHeader}
      />
      <MSpacer mt={8} />
      {isEmptyState ? (
        // Zero-data state: the member has uploaded no blood reports yet, so
        // both the pinned-parameter grid and the constellation snapshot are
        // meaningless. Replace the entire body with a single MEmptyCard
        // whose primary CTA opens the standard document-upload sheet — same
        // sheet the dashboard uses, so the upload experience stays
        // consistent across surfaces.
        <section className={styles.section}>
          <MEmptyCard
            primaryTitle={t("labInsights.emptyStateTitle")}
            secondaryTitle={t("labInsights.emptyStateDescription")}
            primaryCTA={{
              text: t("labInsights.emptyStateCTA"),
              onClick: () => setUploadOpen(true),
            }}
          />
        </section>
      ) : (
        <>
          <section className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <h2 className={styles.sectionTitle}>
                {t("labInsights.pinnedParameters")}
              </h2>
              <div className={styles.legendRow}>
                <span className={styles.legendDot} aria-hidden />
                <span>{t("labInsights.flaggedLegend")}</span>
              </div>
            </div>

            {loading && !data && <MLoader />}

            {error && !loading && (
              <p className={styles.emptyState}>
                {t("labInsights.errorLoading")}
              </p>
            )}

            {!loading && !error && pinned.length === 0 && (
              <p className={styles.emptyState}>
                {t("labInsights.noPinnedParameters")}
              </p>
            )}

            {pinned.length > 0 && (
              <div className={styles.cardGrid}>
                {/* Render 4 grid slots: populated cards first, then blank
                placeholders for any unfilled slots. The blank placeholder
                opens the same Edit drawer so the user can promote any of
                their tested parameters into the pin without leaving the
                landing page. */}
                {Array.from({ length: PIN_SLOT_COUNT }).map((_, idx) => {
                  const card = pinned[idx];
                  if (!card) {
                    return (
                      <button
                        type="button"
                        key={`blank-${idx}`}
                        className={styles.emptyPinSlot}
                        onClick={() => setEditingSlotIndex(idx)}
                        aria-label={t("labInsights.addParameter")}
                      >
                        <span className={styles.emptyPinSlotIcon} aria-hidden>
                          <IconPlus size={16} stroke={2} />
                        </span>
                        <span>{t("labInsights.addParameter")}</span>
                      </button>
                    );
                  }
                  const { value, unit } = formatValue(card);
                  const reading = card.latestReading;
                  return (
                    <MLabParameterCard
                      key={card.canonicalParameterId}
                      // Show the report date as the top label (indigo tone) to
                      // match the lab parameter cards on the group-details and
                      // parameter-details screens. Falls back to the group name
                      // when the pinned parameter has no reading yet (e.g. user
                      // pinned it before the report finished enriching).
                      groupName={
                        formatLabCardDate(reading?.reportDate ?? null) ||
                        card.groupName
                      }
                      topLabelTone="indigo"
                      parameterName={card.canonicalName}
                      value={value}
                      unit={unit}
                      status={reading?.status ?? ParameterStatus.UNKNOWN}
                      flagged={reading?.flagged ?? false}
                      hideFlaggedChip
                      rangeLow={reading?.rangeLow ?? null}
                      rangeHigh={reading?.rangeHigh ?? null}
                      rangeText={reading?.rangeText ?? null}
                      showEditIcon
                      onEditClick={() => setEditingSlotIndex(idx)}
                      onCardClick={() => {
                        saveScroll();
                        navigate(
                          `/lab-insights/parameters/${card.canonicalParameterId}`,
                          { state: { from: "/lab-insights" } }
                        );
                      }}
                    />
                  );
                })}
              </div>
            )}
          </section>

          <div className={styles.lavenderArea}>
            <section className={styles.snapshotSection}>
              <div className={styles.snapshotCard}>
                <div className={styles.sectionHeaderRow}>
                  <h2 className={styles.sectionTitle}>
                    {t("labInsights.snapshot")}
                  </h2>
                  <button
                    type="button"
                    className={styles.seeAll}
                    onClick={handleSeeAll}
                  >
                    {t("labInsights.seeAll")}
                  </button>
                </div>
                <p className={styles.snapshotSubtext}>
                  {t("labInsights.snapshotSubtext")}
                </p>
                {constellation.length > 0 ? (
                  <MLabSnapshot
                    centerLabel={centerLabel}
                    groups={constellation}
                    onGroupClick={handleGroupClick}
                  />
                ) : (
                  <div className={styles.snapshotPlaceholder}>
                    {loading
                      ? t("labInsights.loading")
                      : t("labInsights.errorLoading")}
                  </div>
                )}
              </div>
            </section>

            {interpretationText && (
              <section className={styles.interpretationSection}>
                <div className={styles.interpretationCard}>
                  <h3 className={styles.interpretationTitle}>
                    {t("labInsights.interpretation")}
                  </h3>
                  <p className={styles.interpretationText}>
                    {interpretationText}
                  </p>
                </div>
              </section>
            )}
          </div>
        </>
      )}

      {/* Document upload sheet — mounted at page root so the CTA on the
          empty card can open it without affecting the non-empty layout.
          Mirrors the dashboard's DocumentsCard pattern. */}
      <MDocumentUploadOptions
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSelectOption={() => setUploadOpen(false)}
      />

      <EditPinsDrawer
        open={editPinsOpen}
        memberId={memberInfo?.id}
        currentPinIds={pinned.map((p) => p.canonicalParameterId)}
        onClose={() => setEditingSlotIndex(null)}
        onSave={async (ids) => {
          // The drawer returns a length-1 array with the new (unpinned)
          // parameter the user picked. We replace ONLY the slot the user
          // clicked Edit on — not the last slot, not the first that
          // happens to match. The drawer already rejects clicks on
          // currently-pinned rows so the new id is guaranteed to be
          // a new addition.
          const newId = ids[0];
          if (!newId || editingSlotIndex === null) return;
          const existingIds = pinned.map((p) => p.canonicalParameterId);
          if (existingIds.includes(newId)) return; // defensive — drawer guards too
          const merged = [...existingIds];
          if (editingSlotIndex < merged.length) {
            // Replacing a populated slot.
            merged[editingSlotIndex] = newId;
          } else {
            // User clicked a blank placeholder. Append; recompute on
            // the API side will keep us at or under the 4-pin cap.
            merged.push(newId);
          }
          await updatePins(merged);
        }}
      />
    </div>
  );
};

export default LabInsights;
